import { createImage } from "@/actions/image";
import { createBunnyVideo } from "@/actions/video";
import { PROJECT_URL } from "@/config/project-details";
import { uploadFiles as uploadFilesToUploadthing } from "@/lib/uploadthing";
import { queryClient } from "@/providers/react-query";
import { useUploadStatusStore } from "@/stores/upload-status";
import { TFileType, TUploadedFile, TVideo, ZImageCreateSchema, ZUploadProgress } from "@/types/types";
import { tryCatch } from "@/utils/validators";
import { useAuth } from "@clerk/nextjs";
import axios from "axios";
import { useState } from "react";
import { toast } from "sonner";
import * as tus from "tus-js-client";
type UploadController = {
  abort: (shouldTerminate?: boolean) => void;
};
type TFileUploader = {
  isUploading: boolean;
  progress: ZUploadProgress[];
  uploadFiles: (files: File[], onComplete?: (uploadedFiles: TUploadedFile[]) => void) => Promise<TUploadedFile[]>;
  cancelUpload: (fileId: string, fileType: TFileType, fileKey?: string) => Promise<void>;
};
export function useFileUploader(fileType: TFileType): TFileUploader {
  const [progress, setProgress] = useState<ZUploadProgress[]>([]);
  const [uploads, setUploads] = useState<Map<string, UploadController>>(new Map());
  const {
    isUploading,
    setIsUploading
  } = useUploadStatusStore();
  const {
    userId,
    isLoaded
  } = useAuth();
  if (!userId && isLoaded) {
    return {
      isUploading,
      progress,
      uploadFiles: async () => {
        toast.error("Please log in to upload files.");
        return [];
      },
      cancelUpload: async () => {}
    };
  }
  async function uploadFiles(files: File[], onComplete?: (uploadedFiles: TUploadedFile[]) => void) {
    setIsUploading(true);
    try {
      const uploadPromises = files.map(file => uploadFile(file));
      const results = await Promise.all(uploadPromises);
      const uploadedFiles = results.filter(result => result !== null) as TUploadedFile[];
      if (fileType === "image") {
        queryClient.invalidateQueries({
          queryKey: ["get-images-for-teacher"]
        });
      }
      if (fileType === "video") {
        queryClient.invalidateQueries({
          queryKey: ["get-videos-for-teacher"]
        });
      }
      if (onComplete && uploadedFiles.length > 0) {
        onComplete(uploadedFiles);
      }
      return uploadedFiles;
    } finally {
      setIsUploading(false);
    }
  }
  async function uploadFile(file: File) {
    setProgress(prev => [...prev, {
      fileId: file.name,
      fileKey: "",
      percent: 0,
      status: "Preparing..."
    }]);
    try {
      if (fileType === "image") {
        try {
          const abortController = new AbortController();
          setUploads(prev_1 => new Map(prev_1).set(file.name, {
            abort: () => abortController.abort()
          }));
          const [uploadedFile] = await uploadFilesToUploadthing("paymentReceiptImage", {
            files: [file],
            signal: abortController.signal,
            onUploadBegin: () => {
              setProgress(prev_2 => prev_2.map(p => p.fileId === file.name ? {
                ...p,
                status: "Uploading..."
              } : p));
            },
            onUploadProgress: ({
              progress: progress_0
            }) => {
              const percent = Math.round(progress_0);
              setProgress(prev_3 => prev_3.map(p_0 => p_0.fileId === file.name ? {
                ...p_0,
                percent,
                status: percent === 100 ? "Finalizing..." : "Uploading..."
              } : p_0));
            }
          });
          if (!uploadedFile) {
            toast.error("Image upload failed");
            return null;
          }
          setProgress(prev_4 => prev_4.map(p_1 => p_1.fileId === file.name ? {
            ...p_1,
            status: "Storing..."
          } : p_1));
          const fileProperties = {
            originalName: file.name,
            originalSize: file.size,
            originalType: file.type,
            originalTimestamp: BigInt(file.lastModified)
          };
          const imageProperties: ZImageCreateSchema = {
            ...fileProperties,
            key: uploadedFile.key,
            path: "uploadthing",
            url: uploadedFile.ufsUrl || uploadedFile.url,
            category: "default",
            hostingService: "UploadThing",
            userId: userId!
          };
          const {
            data: createdImage,
            error: error_1
          } = await tryCatch(createImage(imageProperties));
          if (error_1) {
            toast.error(error_1.message);
            return null;
          }
          setProgress(prev_5 => prev_5.filter(p_2 => p_2.fileId !== file.name));
          setUploads(prev_6 => {
            const next_0 = new Map(prev_6);
            next_0.delete(file.name);
            return next_0;
          });

          // toast.success(`Image uploaded: ${file.name}`);
          toast.success("Image uploaded");
          return createdImage;
        } catch (error_0) {
          setUploads(prev_0 => {
            const next = new Map(prev_0);
            next.delete(file.name);
            return next;
          });
          if (error_0 instanceof DOMException && error_0.name === "AbortError") {
            return null;
          }
          console.error("Image upload failed", error_0);
          toast.error("Image upload failed");
          return null;
        }
      }
      if (fileType === "video") {
        // 1️⃣ Create video entry
        const responseA = await axios.post(`${PROJECT_URL}/api/bunny/video`, {
          fileName: file.name
        });
        const videoGuid = responseA.data.guid;
        setProgress(prev_7 => prev_7.map(p_3 => p_3.fileId === file.name ? {
          ...p_3,
          fileKey: videoGuid
        } : p_3));

        // 2️⃣ Upload video file
        const responseB = await axios.post(`${PROJECT_URL}/api/bunny/signature`, {
          videoGuid: videoGuid
        });
        const {
          signature,
          expirationTime,
          videoGuid: _,
          libraryId
        } = responseB.data;
        let createdBunnyVideo: TVideo | null = null;
        await new Promise<void>((resolve, reject) => {
          const upload = new tus.Upload(file, {
            endpoint: "https://video.bunnycdn.com/tusupload",
            retryDelays: [0, 3000, 5000, 10000, 20000, 60000, 60000],
            headers: {
              AuthorizationSignature: signature,
              AuthorizationExpire: expirationTime,
              VideoId: videoGuid,
              LibraryId: libraryId
            },
            metadata: {
              title: file.name,
              filetype: file.type
            },
            onProgress: function (bytesUploaded, bytesTotal) {
              const percent_0 = Math.round(bytesUploaded * 100 / (bytesTotal || 1));
              setProgress(prev_8 => prev_8.map(p_4 => p_4.fileId === file.name ? {
                ...p_4,
                percent: percent_0,
                status: percent_0 === 100 ? "Finalizing..." : "Uploading..."
              } : p_4));
            },
            onSuccess: async function () {
              setProgress(prev_9 => prev_9.map(p_5 => p_5.fileId === file.name ? {
                ...p_5,
                status: "Storing..."
              } : p_5));
              const fileProperties_0 = {
                originalName: file.name,
                originalSize: file.size,
                originalType: file.type,
                originalTimestamp: file.lastModified
              };
              const {
                data: createdBunnyVideoX,
                error: error_2
              } = await tryCatch(createBunnyVideo(videoGuid, fileProperties_0, userId!));
              if (error_2) {
                toast.error(error_2.message);
                return;
              }
              createdBunnyVideo = createdBunnyVideoX;
              setProgress(prev_10 => prev_10.filter(p_6 => p_6.fileId !== file.name));

              // toast.success(`Video uploaded: ${file.name}`);
              toast.success("Video uploaded");
              resolve();
            },
            onError: function (error_3) {
              console.error("Video upload failed", error_3);
              toast.error("Video upload failed");
              reject();
            }
          });
          setUploads(prev_11 => new Map(prev_11).set(file.name, {
            abort: shouldTerminate => upload.abort(shouldTerminate)
          }));
          upload.findPreviousUploads().then(previousUploads => {
            if (previousUploads.length) {
              upload.resumeFromPreviousUpload(previousUploads[0]);
            }
            upload.options.removeFingerprintOnSuccess = true;
            upload.start();
          });
        });
        return createdBunnyVideo;
      }
    } catch (error) {
      console.error("Upload failed", error);
      toast.error("Upload failed");
      return null;
    } finally {
      setTimeout(() => {
        setIsUploading(false);
        setProgress([]);
      }, 3000);
    }
  }
  async function cancelUpload(fileId: string, fileType_0: "video" | "image", fileKey?: string) {
    const upload_0 = uploads.get(fileId);
    try {
      if (upload_0) {
        upload_0.abort(true);
        setUploads(prev_12 => {
          const next_1 = new Map(prev_12);
          next_1.delete(fileId);
          return next_1;
        });
      }
      setProgress(prev_13 => prev_13.filter(p_7 => p_7.fileId !== fileId));
      if (fileType_0 === "image" && fileKey) {
        await axios.delete(`${PROJECT_URL}/api/bunny/image`, {
          params: {
            imageKey: fileKey
          }
        });
      }
      if (fileType_0 === "video" && fileKey) {
        await axios.delete(`${PROJECT_URL}/api/bunny/video`, {
          params: {
            videoGuid: fileKey
          }
        });
      }
    } catch (error_4) {
      console.error(`Failed to cancel upload for ${fileId}`, error_4);
    }
  }
  return {
    isUploading,
    progress,
    uploadFiles,
    cancelUpload
  };
}